import { sentryAddBreadcrumb } from 'utils/sentry';
import { v4 } from 'uuid';

export enum DataObjectType {
    Mention = 'mention',
}

export const MentionTriggerChar = '@';
export const DataObjectTypeAttributeName = 'data-object';
export const DataObjectKeyAttributeName = 'data-object-key';
export const DataMentionIdAttributeName = 'data-mention-id';
export const DataMentionInputAttributeName = 'data-mention-input';

const TagGuid = '[0-9a-fA-F]{8}-(?:[0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}';
const TagOpen = '[{]{2}';
const TagClose = '[}]{2}';

const MentionTagRegex = new RegExp(`${TagOpen}mention:(${TagGuid})${TagClose}`, 'gm');
const WhiteSpaceRegex = /\s/;

export type ContentEditableTextToken = Readonly<{
    type: 'text';
    key: string;
    startIndex: number;
    endIndex: number;
    text: string;
}>;

export type ContentEditableMentionToken = Readonly<{
    type: 'mention';
    key: string;
    startIndex: number;
    endIndex: number;
    mentionId: string;
}>;

export type ContentEditableToken = ContentEditableTextToken | ContentEditableMentionToken;

function parseMentionTokens(text: string): ContentEditableMentionToken[] {
    return Array.from(text.matchAll(MentionTagRegex)).map<ContentEditableMentionToken>(
        (match) => {
            if (match.length !== 2) {
                throw new Error('Missing match details');
            }
            return {
                type: 'mention',
                key: `mention-${match.index}`,
                startIndex: match.index,
                endIndex: match.index + match[0].length - 1,
                mentionId: match[1],
            };
        },
    );
}

export function parseContentEditableTokens(text: string): ContentEditableToken[] {
    const lookup = new Map<number, ContentEditableToken>();

    parseMentionTokens(text).forEach((token) => lookup.set(token.startIndex, token));

    const tokens: ContentEditableToken[] = [];
    let index = 0;
    let buffer = '';
    let startIndex = 0;

    const addTextTokenFromBuffer = () => {
        if (buffer) {
            tokens.push({
                type: 'text',
                key: `text-${startIndex}`,
                startIndex,
                endIndex: index - 1,
                text: buffer,
            });
            buffer = '';
        }
    };

    while (index < text.length) {
        const token = lookup.get(index);
        if (token) {
            addTextTokenFromBuffer();
            tokens.push(token);
            index = token.endIndex + 1;
            startIndex = index;
        } else {
            buffer += text[index];
            index += 1;
        }
    }

    addTextTokenFromBuffer();

    return tokens;
}

export function buildMentionNode(mentionId: string) {
    const mentionNode = document.createElement('span');
    mentionNode.setAttribute(DataObjectKeyAttributeName, v4());
    mentionNode.setAttribute(DataObjectTypeAttributeName, DataObjectType.Mention);
    mentionNode.setAttribute(DataMentionIdAttributeName, mentionId);
    mentionNode.setAttribute('contentEditable', 'false');
    return mentionNode;
}

export function buildPostMentionNodes(mentionId: string) {
    const mentionNode = buildMentionNode(mentionId);
    const textNode = document.createTextNode(' ');
    return [mentionNode, textNode];
}

export function buildTokenNode(token: ContentEditableToken) {
    switch (token.type) {
        case 'text':
            return document.createTextNode(token.text);
        case 'mention':
            return buildMentionNode(token.mentionId);
        default:
            throw new Error('Invalid token type');
    }
}

export function deserializeContentEditableText(text: string): Node[] {
    return parseContentEditableTokens(text).map(buildTokenNode);
}

export function serializeMention(mentionId: string) {
    return `{{${DataObjectType.Mention}:${mentionId}}}`;
}

export function serializeContentEditableText(input: HTMLElement): string {
    return Array.from<ChildNode>(input.childNodes).reduce<string>((result, node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            return result + (node.textContent ?? '');
        }
        if (node instanceof HTMLSpanElement) {
            const objectType = node.getAttribute(DataObjectTypeAttributeName);
            if (objectType === DataObjectType.Mention) {
                const mentionId = node.getAttribute(DataMentionIdAttributeName);
                if (!mentionId) {
                    throw new Error('Missing mention id');
                }
                return `${result}${serializeMention(mentionId)}`;
            }
            return `${result}${serializeContentEditableText(node)}`;
        }
        if (node instanceof HTMLDivElement) {
            return `${result}\n${serializeContentEditableText(node)}`;
        }
        return result;
    }, '');
}

export function isMentionTriggered(event: Event, range: Range) {
    if (!(event instanceof InputEvent)) {
        return false;
    }

    // inputType can be undefined in some browsers/events despite TypeScript's types
    const inputType = event.inputType as string | undefined;
    if (inputType === undefined) {
        sentryAddBreadcrumb({
            category: 'input.mention',
            message: 'InputEvent.inputType is undefined in isMentionTriggered',
            level: 'warning',
        });
        return false;
    }

    return (
        inputType === 'insertText' &&
        event.data === MentionTriggerChar &&
        range.startContainer.textContent !== null &&
        range.endContainer.textContent !== null &&
        // has whitespace or nothing before the selection
        (range.startOffset === 1 ||
            range.startContainer.textContent
                .substring(range.startOffset - 2, range.startOffset - 1)
                .match(WhiteSpaceRegex)) &&
        // has whitespace or nothing after the selection
        (range.endOffset === range.endContainer.textContent.length ||
            range.endContainer.textContent
                .substring(range.endOffset, range.endOffset + 1)
                .match(WhiteSpaceRegex))
    );
}

export function insertSearchElement(caretRange: Range) {
    const input = document.createElement('span');
    input.setAttribute(DataMentionInputAttributeName, '');
    input.textContent = MentionTriggerChar;
    input.autocapitalize = 'off';
    input.spellcheck = false;
    input.contentEditable = 'true';

    // Replace at mention symbol with a content editable span for search
    caretRange.setStart(caretRange.startContainer, caretRange.startOffset - 1);
    caretRange.deleteContents();
    caretRange.insertNode(input);
    caretRange.setEnd(input, 1);
    caretRange.collapse();

    return input;
}
